// GC94 · R02 / C2 — MÓDULO INFORMATIVO COMPARTIDO (mínimo) entre Sitios y Radar.
// Sólo bloques de LECTURA: contexto, plan, contactos, equipos y ejes. Las acciones residenciales y
// administrativas quedan FUERA de esta extracción a propósito.
// Estados diferenciados, nunca mezclados: `undefined` = restringido/no entregado por el alcance;
// `null`/"" = registrado vacío; "error" = fallo de carga; `aplica:false` = no aplicable.
import { CheckCircle2, AlertTriangle, Phone } from "lucide-react";

export const NO_DISPONIBLE = "No disponible para tu rol";
export const NO_REGISTRADO = "No registrado";

export const valor = (v, ausente = NO_DISPONIBLE) =>
  v === undefined ? ausente : v === null || v === "" ? NO_REGISTRADO : v;

export const MODALIDAD_LABEL = { institucional: "Institucional", residencial: "Residencial" };

// C2 · modalidad: se muestra la declaración Y su procedencia. Un dato derivado o ausente nunca se
// presenta como modalidad declarada por el centro.
export function etiquetaModalidad(id = {}) {
  if (!id.modalidad) return "Modalidad no registrada";
  if (id.modalidad_fuente === "declarada") return MODALIDAD_LABEL[id.modalidad] || id.modalidad;
  return `${MODALIDAD_LABEL[id.modalidad] || id.modalidad} (no declarada por el centro)`;
}

// C2 · disponibilidad: si consta que acude por horario pero no el intervalo, se dice exactamente eso.
export function etiquetaDisponibilidad(c = {}) {
  if (c.puede_acudir === "si") return "Puede acudir";
  if (c.puede_acudir === "no") return "No puede acudir";
  if (c.puede_acudir === "horario") {
    return c.restriccion_horaria ? `Acude en horario: ${c.restriccion_horaria}`
      : "Acude por horario · Horario no especificado";
  }
  return c.puede_acudir === undefined ? null : "Disponibilidad no registrada";
}

export const NIVEL_AUTORIDAD_LABEL = {
  informativo: "Informativo",
  autorizado: "Autorizado",
  responsable_autorizado_plan: "Responsable autorizado del plan",
};

// E1 · se lee el nivel canónico y se distingue lo capturado de lo predeterminado por ausencia.
export function etiquetaAutoridad(c = {}) {
  const nivel = c.nivel_autoridad ?? c.autoridad;
  if (!nivel) return null;
  const txt = NIVEL_AUTORIDAD_LABEL[nivel] || nivel;
  if (c.nivel_autoridad_origen === "predeterminado") return `${txt} (sin autorización capturada)`;
  if (c.nivel_autoridad_origen === "legacy_no_reconocido") return `${nivel} (nivel no reconocido)`;
  return txt;
}

const COMM_LABEL = {
  LISTO: "Commissioning LISTO", PENDIENTE: "Commissioning pendiente", BLOQUEADO: "Plan bloqueado",
};

export const SALUD_LABEL = {
  confirmado_operativo: "Operación confirmada", confirmado_caido: "Caído (confirmado)",
};

// ---------------------------------------------------------------- Plan vigente (P1 · P2 · P4)
export function BloquePlan({ plan, testId = "bloque-plan" }) {
  if (plan === undefined) {
    return <p data-testid={`${testId}-restringido`} className="text-sm text-slate-500">{NO_DISPONIBLE}</p>;
  }
  if (plan === "error") {
    return <p data-testid={`${testId}-error`} className="text-sm text-slate-600">No se pudo cargar el plan. Vuelve a intentarlo.</p>;
  }
  // P1 · un actor acotado recibe SIEMPRE este mismo texto, sin distinguir ausencia de restricción.
  if (plan?.correspondencia === "no_disponible") {
    return <p data-testid={`${testId}-no-disponible`} className="text-sm text-slate-500">No hay plan de respuesta disponible para esta persona en tu alcance.</p>;
  }
  if (plan?.existe === false) {
    return <p data-testid={`${testId}-sin-plan`} className="text-sm text-amber-700">Esta persona aún no tiene plan configurado.</p>;
  }
  const ajeno = plan.aplica_al_sitio_actual === false;
  return (
    <div className="space-y-2" data-testid={testId}>
      {ajeno && (
        <p data-testid={`${testId}-advertencia-sitio`} className="rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-xs text-amber-800">
          {plan.correspondencia === "otro_sitio_autorizado"
            ? `Plan registrado en otro centro autorizado${plan.sitio_origen_nombre ? ` (${plan.sitio_origen_nombre})` : ""}: no corresponde al sitio actual de la persona.`
            : "El plan no tiene sitio registrado: no se puede verificar su correspondencia con el sitio actual."}
        </p>
      )}
      <p data-testid={`${testId}-estado`} className={`text-sm font-medium ${plan.commissioning_status === "LISTO" && !ajeno ? "text-emerald-600" : "text-amber-700"}`}>
        Versión {valor(plan.version)} · {COMM_LABEL[plan.commissioning_status] || valor(plan.commissioning_status)}
        {!ajeno && plan.aplica_al_sitio_actual ? " · corresponde al sitio actual" : ""}
      </p>
      {Array.isArray(plan.commissioning_faltantes) && plan.commissioning_faltantes.length > 0 && (
        <ul data-testid={`${testId}-faltantes`} className="text-xs text-amber-700 list-disc pl-5 space-y-0.5">
          {plan.commissioning_faltantes.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
      {plan.instrucciones_especiales && (
        <p data-testid={`${testId}-instrucciones`} className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">{plan.instrucciones_especiales}</p>
      )}
      {/* P4 · responsables, autorizante y familias son CONSULTABLES en Sitios y en Radar: no hay
          variante reducida que los omita. */}
      <div className="grid gap-3 sm:grid-cols-2 text-sm text-slate-700 dark:text-slate-200">
        <p data-testid={`${testId}-responsable`}><span className="text-slate-400">Responsable del plan: </span>{valor(plan.responsable_plan)}</p>
        <p data-testid={`${testId}-autorizante`}><span className="text-slate-400">Autorizado por: </span>{valor(plan.autorizado_por)}</p>
      </div>
      {plan.decision_emergencia && (
        <p className="text-xs text-slate-500">Emergencia: {plan.decision_emergencia}</p>
      )}
      {Array.isArray(plan.familias_activas) && plan.familias_activas.length > 0 && (
        <div data-testid={`${testId}-familias`} className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Familias activas y su acción configurada</p>
          {plan.familias_activas.map((fam) => {
            const cfg = (plan.plantillas_por_tipo || {})[fam] || {};
            return (
              <p key={fam} className="text-sm text-slate-700 dark:text-slate-200">
                <b>{fam}</b>: {cfg.accion_final_sin_verificacion || NO_REGISTRADO}
                {cfg.accion_si_compromiso_vence ? ` · al vencer la espera: ${cfg.accion_si_compromiso_vence}` : ""}
              </p>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Contactos de atención (P4 · P5)
export function BloqueContactos({ contactos, testId = "bloque-contactos" }) {
  // P5 · centinelas fijos: undefined = no entregado por alcance; [] = registrado vacío;
  // "error" = tipo inesperado detectado por el adaptador. Nunca se confunden entre sí.
  if (contactos === undefined) {
    return <p data-testid={`${testId}-restringido`} className="text-sm text-slate-500">{NO_DISPONIBLE}</p>;
  }
  // null y "" son VACÍO REGISTRADO por el backend; sólo un tipo inesperado es error.
  if (contactos === null || contactos === "" || (Array.isArray(contactos) && !contactos.length)) {
    return <p data-testid={`${testId}-vacio`} className="text-sm text-amber-700">No hay contactos vinculados a esta persona.</p>;
  }
  if (!Array.isArray(contactos)) {
    return <p data-testid={`${testId}-error`} className="text-sm text-slate-600">No se pudieron cargar los contactos.</p>;
  }
  return (
    <ul className="space-y-2" data-testid={testId}>
      {contactos.map((c, i) => (
        <li key={i} data-testid={`contacto-${i}`} className="rounded-xl border border-gray-100 dark:border-slate-700 p-3">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 break-words [overflow-wrap:anywhere]">
            {c.orden ? `${c.orden}. ` : ""}{c.nombre || "(sin nombre)"}
          </p>
          {/* P4 · relación y función son datos DISTINTOS: se muestran ambos cuando existen, y los
              booleanos se publican en sus dos estados (un campo omitido no se lee como false). */}
          <p data-testid={`contacto-${i}-atributos`} className="text-xs text-slate-500 mt-0.5 break-words">
            {[c.relacion, c.funcion, etiquetaAutoridad(c), etiquetaDisponibilidad(c),
              c.recibe_avisos === undefined ? null : c.recibe_avisos ? "recibe avisos" : "no recibe avisos",
              c.puede_confirmar_estado === undefined ? null
                : c.puede_confirmar_estado ? "puede confirmar estado" : "no confirma estado",
            ].filter(Boolean).join(" · ")}
          </p>
          {c.telefono && (
            <a href={`tel:${c.telefono}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0891A6]">
              <Phone className="h-3 w-3" /> {c.telefono}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- Equipos / cobertura
export function BloqueEquipos({ dispositivos, testId = "bloque-equipos" }) {
  if (dispositivos === undefined) {
    return <p data-testid={`${testId}-restringido`} className="text-sm text-slate-500">{NO_DISPONIBLE}</p>;
  }
  if (dispositivos === null || dispositivos === "" || (Array.isArray(dispositivos) && !dispositivos.length)) {
    return <p data-testid={`${testId}-vacio`} className="text-sm text-amber-700">Ningún equipo asignado a esta persona.</p>;
  }
  if (!Array.isArray(dispositivos)) {
    return <p data-testid={`${testId}-error`} className="text-sm text-slate-600">No se pudo cargar la cobertura técnica.</p>;
  }
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {dispositivos.map((d, i) => (
        <li key={i} data-testid={`dispositivo-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700 dark:text-slate-200">
          <span className="font-medium">{d.tipo || "Equipo"}</span>
          <span className="text-slate-500">· {d.denominacion || NO_REGISTRADO}</span>
          <span className="text-slate-500">· asignación {d.asignacion === "compartida" ? "compartida" : "individual"}</span>
          <span className="text-slate-500" title={d.salud_motivo || undefined}>· {SALUD_LABEL[d.salud_estado] || "No confirmable"}</span>
          {Array.isArray(d.capacidades) && d.capacidades.length > 0 && (
            <span className="text-xs text-slate-400">({d.capacidades.join(", ")})</span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- Ejes de preparación
export function Eje({ nombre, eje, testId }) {
  if (eje === undefined) {
    return <p data-testid={testId} className="text-sm text-slate-500">{nombre}: {NO_DISPONIBLE}</p>;
  }
  if (eje && eje.estado_lectura) {
    return (
      <p data-testid={testId} className="text-sm text-slate-500">
        {nombre}: {eje.estado_lectura === "restringido" ? "restringido" : "no verificable"}
        {eje.advertencia ? ` · ${eje.advertencia}` : ""}
      </p>
    );
  }
  if (eje && eje.aplica === false) {
    return (
      <p data-testid={testId} className="text-sm text-slate-500">
        {nombre}: no aplica{eje.no_aplica_motivo ? ` · ${eje.no_aplica_motivo}` : ""}
      </p>
    );
  }
  const { completo, faltantes } = eje || {};
  return (
    <div data-testid={testId}>
      <p className={`text-sm font-medium flex items-center gap-1.5 ${completo ? "text-emerald-600" : completo === false ? "text-amber-700" : "text-slate-500"}`}>
        {completo ? <CheckCircle2 className="h-4 w-4" /> : completo === false ? <AlertTriangle className="h-4 w-4" /> : null}
        {nombre}: {completo ? "completo" : completo === false ? "incompleto" : "sin dato"}
      </p>
      {Array.isArray(faltantes) && faltantes.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-amber-700 list-disc pl-5">
          {faltantes.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
    </div>
  );
}

export function BloqueEjes({ readiness, identidad, modalidadFuente, testId = "bloque-ejes" }) {
  if (readiness === undefined) {
    return <p data-testid={`${testId}-restringido`} className="text-sm text-slate-500">{NO_DISPONIBLE}</p>;
  }
  if (readiness === "error" || typeof readiness !== "object") {
    return <p data-testid={`${testId}-error`} className="text-sm text-slate-600">No se pudo cargar la preparación.</p>;
  }
  // B (cierre final) · la IDENTIDAD es la referencia de modalidad: encabezado y ejes presentan el
  // mismo valor y la misma procedencia aunque el agregado de readiness traiga otra cosa.
  const modalidad = (identidad || {}).modalidad ?? readiness.modalidad;
  const fuente = (identidad || {}).modalidad_fuente ?? modalidadFuente;
  return (
    <div className="space-y-2" data-testid={testId}>
      <p data-testid="readiness-estado" className="text-sm text-slate-600 dark:text-slate-300">
        Estado: <b>{readiness.etiqueta_estado || readiness.estado_label || valor(readiness.estado)}</b>
        {modalidad ? ` · ${etiquetaModalidad({ modalidad, modalidad_fuente: fuente })}` : ""}
      </p>
      <Eje nombre="Expediente administrativo" eje={readiness.admin_operativo} testId="readiness-eje-admin" />
      <Eje nombre="Cobertura técnica" eje={readiness.tecnico_cobertura} testId="readiness-eje-tecnico" />
      <Eje nombre="Plan de respuesta" eje={readiness.plan_respuesta} testId="readiness-eje-plan" />
      <p className="text-xs text-slate-400">
        Un plan completo sigue completo aunque la cobertura técnica esté pendiente: los ejes se declaran por separado.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- Contexto de atención
export function BloqueContexto({ contexto, instrucciones, contextoHabitacion, testId = "bloque-contexto" }) {
  if (contexto === undefined && instrucciones === undefined) {
    return <p data-testid={`${testId}-restringido`} className="text-sm text-slate-500">{NO_DISPONIBLE}</p>;
  }
  const ctx = contexto || {};
  return (
    <div className="space-y-3" data-testid={testId}>
      {contextoHabitacion?.vigencia === "actual" && (
        <p data-testid={`${testId}-vigencia`} className="text-xs text-slate-400">
          Habitación e indicaciones consultadas hoy
          {contextoHabitacion.consultado_en ? ` · ${new Date(contextoHabitacion.consultado_en).toLocaleString("es-MX")}` : ""}
        </p>
      )}
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Indicaciones individuales</p>
        <p data-testid="dato-indicaciones" className="text-sm text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {valor(ctx.indicaciones ?? instrucciones)}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Condiciones que afectan la verificación</p>
          <p data-testid="dato-condiciones" className="text-sm text-slate-700 dark:text-slate-200 mt-1 break-words">{valor(ctx.condiciones)}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Responsable operativo del centro</p>
          <p data-testid="dato-responsable" className="text-sm text-slate-700 dark:text-slate-200 mt-1 break-words">{valor(ctx.responsable_operativo)}</p>
        </div>
      </div>
      {Array.isArray(ctx.turnos) && ctx.turnos.length > 0 && (
        <ul data-testid="ficha-turnos" className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
          {ctx.turnos.map((t, i) => (
            <li key={i}>{[t.nombre || t.turno, t.responsable, t.horario || [t.desde, t.hasta].filter(Boolean).join("–")].filter(Boolean).join(" · ")}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
